/**
 * The shell's one overlay primitive — a bottom sheet on phones, a centred
 * panel on desktop, and a full-height page when `variant="full"`.
 *
 * The repo has no Radix and no portal primitive (see the note in
 * `components/notifications/NotificationBell.tsx`), so this is hand-rolled the
 * same way every other overlay here is: fixed positioning, a backdrop that
 * closes on click, Escape to dismiss, and a body-scroll lock while open.
 *
 * SIXTY FRAMES
 * ------------
 * Only `transform` and `opacity` are animated — never `height`, `top` or
 * `width`, which force layout on every frame. The backdrop fades, the panel
 * slides on `translateY`, and both are compositor-friendly. `prefers-reduced-
 * motion` collapses every duration to zero rather than merely shortening it.
 *
 * CENTRING IS DONE BY FLEXBOX, NOT BY `translate`. Framer writes the panel's
 * `transform` inline to animate it, which silently overrides any Tailwind
 * `-translate-x-1/2` centring — the panel would animate into the wrong place on
 * desktop and nowhere obvious would say why. The wrapper does the positioning;
 * the panel only ever owns its own transform.
 *
 * DRAG-TO-DISMISS
 * ----------------
 * Bottom sheets (`variant="sheet"`) can be pulled down past a threshold, or
 * flicked, to close — `sheetDrag.ts` (pure, unit-tested beside this file)
 * decides when a release counts as a dismiss and how far the backdrop should
 * have faded mid-drag. The panel drags on `y` with `dragConstraints={{ top: 0,
 * bottom: 0 }}` and `dragElastic={{ top: 0, bottom: 1 }}`: pinned at the top
 * (no rubber-band upward past rest), free-moving downward. When a drag ends
 * without crossing the dismiss threshold, framer's own constraint enforcement
 * springs the panel back to 0 — nothing here has to animate that by hand.
 * `variant="full"` (the entry sheet) is not draggable: it is usually a form,
 * and a stray downward swipe while filling one in should scroll it, not close
 * it. Escape and backdrop-click keep working exactly as before, on every
 * variant.
 */
import { useEffect, useRef, type ReactNode } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  type PanInfo,
} from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { backdropOpacityForDrag, shouldDismissSheet } from "./sheetDrag";

export interface SheetProps {
  open: boolean;
  onClose(): void;
  /** Announced to screen readers and drawn as the sheet's heading. */
  title: string;
  /** `sheet` = bottom sheet (max 88vh). `full` = full height, for entry. */
  variant?: "sheet" | "full";
  /** Hide the built-in header row when the content draws its own. */
  bareHeader?: boolean;
  children: ReactNode;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  variant = "sheet",
  bareHeader = false,
  children,
  className,
}: SheetProps) {
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropOpacity = useMotionValue(1);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    // Move focus into the panel so Escape and Tab land somewhere sensible.
    panelRef.current?.focus();
    // Fresh sheet, fresh backdrop — undoes any mid-drag fade left over from a
    // drag that didn't complete before the sheet was reopened.
    backdropOpacity.set(1);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, backdropOpacity]);

  const transition = reduceMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 380, damping: 34, mass: 0.7 };

  const isFull = variant === "full";
  const offset = reduceMotion ? 0 : isFull ? "100%" : 28;
  const draggable = !isFull && !reduceMotion;

  function handleDrag(_event: unknown, info: PanInfo) {
    backdropOpacity.set(backdropOpacityForDrag(info.offset.y));
  }

  function handleDragEnd(_event: unknown, info: PanInfo) {
    if (shouldDismissSheet({ offsetY: info.offset.y, velocityY: info.velocity.y })) {
      onClose();
      return;
    }
    // Not dismissed — the panel itself springs back to 0 via dragConstraints;
    // only the backdrop needs to be told the drag is over.
    backdropOpacity.set(1);
  }

  return (
    <AnimatePresence>
      {open && (
        <div
          className={cn(
            "fixed inset-0 z-[60] flex justify-center",
            isFull ? "items-stretch sm:items-center sm:p-6" : "items-end sm:items-center sm:p-6",
          )}
          role="presentation"
        >
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            style={{ opacity: backdropOpacity }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.16 }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            drag={draggable ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 1 }}
            dragMomentum={false}
            onDrag={draggable ? handleDrag : undefined}
            onDragEnd={draggable ? handleDragEnd : undefined}
            className={cn(
              // `relative` lifts it above the absolutely-positioned backdrop
              // without a second z-index to keep in step.
              "relative flex w-full flex-col overflow-hidden bg-[var(--cp-surface)] outline-none",
              isFull
                ? // A full-height sheet covers the status bar, so it owns the
                  // top inset itself; the desktop panel is inside a padded
                  // wrapper and must not double it.
                  "h-full pt-[env(safe-area-inset-top)] sm:h-full sm:max-h-[46rem] sm:rounded-[var(--cp-r-card)] sm:border sm:border-[var(--cp-line)] sm:pt-0 sm:shadow-[var(--cp-shadow-lg)]"
                : "max-h-[88vh] rounded-t-[20px] border-t border-[var(--cp-line)] shadow-[var(--cp-shadow-lg)] sm:max-h-[80vh] sm:rounded-[var(--cp-r-card)] sm:border",
              // Wallet is a phone app, but this must not look like a stretched
              // phone on a 27" monitor.
              "sm:w-[min(30rem,100%)]",
              className,
            )}
            initial={{ y: offset, opacity: reduceMotion ? 1 : 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: offset, opacity: reduceMotion ? 1 : 0 }}
            transition={transition}
            style={{ willChange: "transform, opacity" }}
          >
            {!bareHeader && (
              <div className="relative flex shrink-0 items-center justify-between gap-3 border-b border-[var(--cp-line-2)] px-4 py-3">
                {!isFull && (
                  <span
                    aria-hidden="true"
                    className="absolute left-1/2 top-2 h-1 w-9 -translate-x-1/2 rounded-full bg-[var(--cp-line-2)] sm:hidden"
                  />
                )}
                <h2 className="text-[15px] font-semibold text-[var(--cp-ink)]">{title}</h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="cp-icon-btn h-8 w-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent-wash)]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto overscroll-contain",
                !isFull && "pb-[max(1rem,env(safe-area-inset-bottom))]",
              )}
            >
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
